import { LoginFormTypes, LoginPageProps, useTranslate } from "@refinedev/core";
import {
  CardProps,
  Form,
  FormProps,
  Input,
  LayoutProps,
  Typography,
} from "antd";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useCheckVerifyEmail } from "../../../../api";
// import { ReactComponent as SmartLogo } from "../../../../common/images/svg/SmartIcon.svg";
import { ReactComponent as SmartLogo } from "../../../../common/images/svg/SmartIconHover.svg";
import { CustomButton } from "../../../../components/button/buttonCustom";
import { ButtonTransparent } from "../../../../components/button/buttonTransparent";
import LoadingOverlay from "../../../../components/loading/loadingOverlay";
import { useNotification } from "../../../../components/providers/notification/useNotification";
import {
  clearFieldErrorMessageOnChange
} from "../../../../utils/form.constants";
import useAuth from "../../../../utils/hooks/useAuth";
import useResponsive from "../../../../utils/hooks/useResponsive";
import { ThemedTitle } from "../../../themedLayout/title";
import { PageTitleStyles } from "./styles";

type LoginProps = LoginPageProps<LayoutProps, CardProps, FormProps>;

export const LoginPage: React.FC<LoginProps> = ({ formProps, title }) => {
  const [form] = Form.useForm<LoginFormTypes>();
  const { isTablet } = useResponsive();
  const navigate = useNavigate();
  const translate = useTranslate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const email = queryParams.get("email");
  const { open: openNotification } = useNotification();
  const hasRunRef = useRef(false);

  function decodeEmail(encodedEmail: string) {
    if (!encodedEmail) return "";
    let decoded = decodeURIComponent(encodedEmail);
    decoded = decoded.replace(/\s/g, "+");
    return decoded;
  }

  const { displayMode } = useParams();
  const [isTouched, setIsTouched] = useState(false);

  const { data } = useCheckVerifyEmail(decodeEmail(email || ""));

  const { login, loadingLogin } = useAuth();

  const PageTitle =
    title === false ? null : (
      <div
        style={{
          ...PageTitleStyles,
        }}
      >
        {title ?? <ThemedTitle collapsed={false} />}
      </div>
    );

  const onFinish = useCallback(
    (values: LoginFormTypes) => {
      login({
        email: values.email ?? "",
        password: values.password ?? "",
      });
    },
    [displayMode, login]
  );

  const handleKeyDown = (event: any) => {
    if (event.key === " ") {
      event.preventDefault();
    }
  };

  useEffect(() => {
    setIsTouched(
      !!form.getFieldValue("email") || !!form.getFieldValue("password")
    );
  }, []);

  useEffect(() => {
    if (!data || !email) return;
    if (hasRunRef.current) return;
    hasRunRef.current = true;
    if (openNotification) {
      openNotification?.({
        key: "verified",
        message: data?.data?.message || "Success",
        type: "success",
      });
    }
  }, [data]);

  const CardContent = (
    <Form
      form={form}
      onFinish={onFinish}
      requiredMark={false}
      style={{ width: "100%" }}
      {...formProps}
      onValuesChange={() => {
        clearFieldErrorMessageOnChange(form);
        setIsTouched(
          !!form.getFieldValue("email") || !!form.getFieldValue("password")
        );
      }}
    >
      <div className="auth-form">
        <Form.Item
          label="Email"
          name="email"
          validateTrigger="onBlur"
          rules={[
            // onlyLatinCharsDigitsSpecialSymbolsRule,
            {
              required: true,
              validateTrigger: "onBlur",
              message: "This field is required",
            },
            {
              type: "email",
              message: translate(
                "pages.login.errors.validEmail",
                "Invalid email address"
              ),
              validateTrigger: "onBlur",
            },
          ]}
        >
          <Input
            readOnly
            onFocus={(e) => e.currentTarget.removeAttribute("readonly")}
            placeholder={translate(
              "pages.login.fields.email",
              "Enter email address"
            )}
            spellCheck={false}
          />
        </Form.Item>
        <div className="password-box">
          <Form.Item
            className="input-password"
            name="password"
            validateTrigger="onBlur"
            label="Password"
            rules={[
              // onlyLatinCharsDigitsSpecialSymbolsRule,
              {
                required: true,
                message: "This field is required",
              },
              // {
              //   validator: (_, value) => {
              //     if (!value) return Promise.resolve();
              //     if (value.length >= 6) {
              //       return Promise.resolve();
              //     }
              //     return Promise.reject(new Error("6 characters at least"));
              //   },
              // },
            ]}
          >
            <Input.Password
              readOnly
              onFocus={(e) => e.currentTarget.removeAttribute("readonly")}
              placeholder="Enter your account password"
              onKeyDown={handleKeyDown}
            />
          </Form.Item>
          <Typography
            style={{
              color: "#1DABF1",
              fontSize: isTablet ? 12 : 14,
              cursor: "pointer",
              width: "fit-content",
            }}
            onClick={() => navigate("/forgot-password")}
          >
            {translate(
              "pages.login.buttons.forgotPassword",
              "Forget password?"
            )}
          </Typography>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <CustomButton
            type="submit"
            width={"100%"}
            size={isTablet ? "medium" : "large"}
            text={translate("pages.login.signin", "Sign in")}
            disabled={!isTouched}
          />
          <ButtonTransparent
            width={"100%"}
            size={isTablet ? "medium" : "large"}
            text={"Registration"}
            onClick={() => {
              if (loadingLogin) return;
              navigate("/register");
            }}
            textClassName="text-button-back"
          />
        </div>
      </div>
    </Form>
  );

  return (
    <div className="auth-box">
      <div className="auth-render">
        {PageTitle}
        {CardContent}
      </div>
      <SmartLogo className="auth-logo" style={{ width: 39, height: 39, marginLeft: 8 }} />
      <LoadingOverlay loading={loadingLogin} />
    </div>
  );
};
